import { httpDelete, httpGet, httpPatch, httpPost } from './httpClient';

interface HydraCollection<T> {
  'hydra:member'?: T[];
  member?: T[];
}

export interface AlarmTypeLite {
  id: number;
  code?: string;
  label?: string;
}

export type AlarmRuleScope = 'box' | 'device' | 'attribute' | 'attribute_type';

export interface ApiAlarmRule {
  id: number;
  enabled: boolean;
  scope: AlarmRuleScope;
  operator?: '>' | '<' | '=' | '!=';
  thresholdValue?: string | null;
  offlineAfterSeconds?: number | null;
  cooldownSeconds?: number | null;
  createdAt: string;
  updatedAt: string;

  project: string;
  type: AlarmTypeLite | string;

  box?: { id: number; name?: string; code?: string } | string | null;
  device?: { id: number; name?: string; code?: string } | string | null;
  attribute?: { id: number; name?: string } | string | null;
  attributeType?: { id: number; code?: string; name?: string } | string | null;
}

export interface BoxLite {
  id: number;
  name: string;
  code: string;
}

export interface DeviceLite {
  id: number;
  name: string;
  code: string;
}

export interface AttributeTypeLite {
  id: number;
  code?: string;
  name?: string;
}

export interface AttributeLite {
  id: number;
  name: string;
  device?: { id: number; name?: string; code?: string } | string;
  type?: AttributeTypeLite | string;
}

export async function getAlarmTypes(): Promise<AlarmTypeLite[]> {
  const res = await httpGet<HydraCollection<AlarmTypeLite>>('/alarm_types');
  return res['hydra:member'] || res.member || [];
}

export async function getAlarmRules(params: {
  projectIri?: string;
  enabled?: boolean;
  typeIri?: string;
  scope?: AlarmRuleScope;
}): Promise<ApiAlarmRule[]> {
  const res = await httpGet<HydraCollection<ApiAlarmRule>>('/alarm_rules', {
    ...(params.projectIri ? { project: params.projectIri } : {}),
    ...(params.enabled !== undefined ? { enabled: params.enabled } : {}),
    ...(params.typeIri ? { type: params.typeIri } : {}),
    ...(params.scope ? { scope: params.scope } : {}),
  });
  return res['hydra:member'] || res.member || [];
}

export async function getBoxes(params: { projectIri?: string }): Promise<BoxLite[]> {
  const res = await httpGet<HydraCollection<BoxLite>>('/boxes', {
    ...(params.projectIri ? { project: params.projectIri } : {}),
    pagination: false,
  });
  return res['hydra:member'] || res.member || [];
}

export async function getDevices(params: { projectIri?: string }): Promise<DeviceLite[]> {
  const res = await httpGet<HydraCollection<DeviceLite>>('/devices', {
    ...(params.projectIri ? { project: params.projectIri } : {}),
    pagination: false,
  });
  return res['hydra:member'] || res.member || [];
}

export async function getAttributes(params: { projectIri?: string }): Promise<AttributeLite[]> {
  const res = await httpGet<HydraCollection<AttributeLite>>('/attributes', {
    ...(params.projectIri ? { project: params.projectIri } : {}),
    pagination: false,
    // ask API Platform to embed device/type when possible
    'groups[]': ['attribute:read', 'device:read'],
  });
  return res['hydra:member'] || res.member || [];
}

export async function getAttributeTypes(): Promise<AttributeTypeLite[]> {
  const res = await httpGet<HydraCollection<AttributeTypeLite>>('/attribute_types', {
    pagination: false,
    'groups[]': ['attributeType:read'],
  });
  return res['hydra:member'] || res.member || [];
}

export async function createAlarmRule(payload: {
  project: string;
  type: string;
  scope: AlarmRuleScope;
  box?: string;
  device?: string;
  attribute?: string;
  attributeType?: string;
  operator?: '>' | '<' | '=' | '!=';
  thresholdValue?: string | number;
  offlineAfterSeconds?: number;
  cooldownSeconds?: number | null;
  enabled: boolean;
}): Promise<unknown> {
  return await httpPost('/alarm_rules', payload);
}

export async function patchAlarmRuleEnabled(id: number, enabled: boolean): Promise<unknown> {
  return await httpPatch(`/alarm_rules/${id}`, { enabled });
}

export async function deleteAlarmRule(id: number): Promise<void> {
  await httpDelete(`/alarm_rules/${id}`);
}

